import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  HiX, 
  HiDownload, 
  HiOutlineDocument, 
  HiOutlinePhotograph, 
  HiOutlineFolderOpen,
  HiOutlineArrowsExpand,
  HiOutlineEmojiSad
} from "react-icons/hi";
import axios from "axios";
import { viewFileAPI, downloadFileAPI } from "../services/driveService";
import useAuth from "../useAuth";

const ViewFileModal = ({ file, isOpen, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && file) {
      loadFile();
    } else {
      // Cleanup
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setError(null);
    }
  }, [isOpen, file]);

  const loadFile = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("dbToken");
      const url = viewFileAPI(file.id);
      
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const urlObject = URL.createObjectURL(response.data);
      setBlobUrl(urlObject);
    } catch (err) {
      console.error("Error loading file preview:", err);
      setError("Failed to load preview. You can still download the file.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (e) => {
    if (e) e.stopPropagation();
    try {
      const token = localStorage.getItem("dbToken");
      const url = downloadFileAPI(file.id);
      
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const urlObject = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = urlObject;
      link.download = file?.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(urlObject);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Please try again.");
    }
  };

  const isImage = (ext) => ["jpg", "jpeg", "png", "gif", "svg"].includes(ext?.toLowerCase());
  const isPdf = (ext) => ext?.toLowerCase() === "pdf";

  const extension = file?.extension || file?.name?.split('.').pop()?.toLowerCase();

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-6xl max-h-full bg-white rounded-[2rem] shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white/50 backdrop-blur-md sticky top-0 z-10">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                  {isImage(extension) ? <HiOutlinePhotograph size={24} /> : <HiOutlineDocument size={24} />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800 leading-tight truncate max-w-[200px] md:max-w-md">
                    {file?.name}
                  </h3>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    {file?.size} • {extension}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleDownload}
                  className="p-3 hover:bg-slate-100 rounded-2xl text-slate-600 transition-all flex items-center gap-2 font-bold text-sm"
                >
                  <HiDownload size={20} />
                   <span className="hidden sm:inline">Download</span>
                </button>
                <button 
                  onClick={onClose}
                  className="p-3 bg-slate-100 hover:bg-slate-200 rounded-2xl text-slate-600 transition-all"
                >
                  <HiX size={20} />
                </button>
              </div>
            </div>

            {/* Preview Area */}
            <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center min-h-[50vh] relative group">
              {loading && (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                  <p className="text-slate-400 font-bold animate-pulse">Fetching preview...</p>
                </div>
              )}

              {error && (
                <div className="text-center p-8 max-w-sm">
                  <div className="w-16 h-16 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-4">
                    <HiOutlineEmojiSad size={32} />
                  </div>
                  <h4 className="text-lg font-bold text-slate-800 mb-2">Preview Unavailable</h4>
                  <p className="text-sm text-slate-500 mb-6">{error}</p>
                </div>
              )}

              {!loading && !error && blobUrl && (
                <>
                  {isImage(extension) ? (
                    <img 
                      src={blobUrl} 
                      alt={file?.name} 
                      className="max-w-full max-h-[70vh] object-contain shadow-2xl rounded-lg"
                    />
                  ) : isPdf(extension) ? (
                    <iframe 
                      src={`${blobUrl}#toolbar=0`} 
                      className="w-full h-[75vh] border-none"
                      title="PDF Preview"
                    />
                  ) : (
                    <div className="text-center p-12">
                       <div className="w-24 h-24 bg-indigo-50 text-indigo-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-indigo-100">
                         <HiOutlineFolderOpen size={48} />
                       </div>
                       <h4 className="text-2xl font-black text-slate-800 mb-2">Can't preview this file</h4>
                       <p className="text-slate-400 font-medium mb-8">This file type ({extension}) doesn't support direct previews yet.</p>
                       <button 
                        onClick={handleDownload}
                        className="px-8 py-4 bg-indigo-600 text-white rounded-[1.5rem] font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-3 mx-auto"
                       >
                         <HiDownload size={20} />
                         Download to View
                       </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer / Tip */}
            <div className="p-4 bg-white border-t border-slate-100 flex justify-center">
               <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-2">
                 <HiOutlineArrowsExpand />
                 Use mouse wheel to zoom (Coming soon) • Press ESC to close
               </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ViewFileModal;
